/* eslint-disable unicode-bom */
import React, { useState, useEffect } from 'react';

const ValidatorsList = ({ rpcUrl = 'http://localhost:9545' }) => {
  const [validators, setValidators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedValidator, setExpandedValidator] = useState(null);
  const [reputations, setReputations] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch validators
        const response = await fetch(rpcUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getValidators',
            params: [],
            id: 1,
          }),
        });
        const data = await response.json();
        if (data.result && Array.isArray(data.result)) {
          const validatorsList = data.result
            .filter(validator => validator.status === 'Active') // Only active validators
            .map(validator => ({
              id: validator.id,
              address: validator.address,
              total_stake: parseFloat(validator.total_stake) || 0,
              delegator_count: parseInt(validator.delegator_count) || 0,
              apr: 12, // 12% APR for validators
              status: validator.status || 'Active',
              rewards: parseFloat(validator.total_rewards) || 0
            }));
          setValidators(validatorsList);
          
          // Fetch reputation for each validator
          const repData = {};
          for (const validator of validatorsList) {
            try {
              const repResponse = await fetch(rpcUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  jsonrpc: '2.0',
                  method: 'avo_getValidatorReputation',
                  params: [validator.address],
                  id: 2,
                }),
              });
              const repResult = await repResponse.json();
              if (repResult.result) {
                repData[validator.address] = repResult.result;
              }
            } catch (err) {
              console.warn(`Could not fetch reputation for ${validator.address}`);
            }
          }
          setReputations(repData);
        } else {
          setValidators([]);
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setValidators([]);
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [rpcUrl]);

  const filteredValidators = validators.filter(validator => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return validator.id.toString().includes(search) || validator.address.toLowerCase().includes(search);
  });

  const formatStake = (amount) => {
    return `${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })} AVO`;
  };

  const formatRewards = (amount) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })} AVO`;
  };

  const renderStars = (score) => {
    if (!score) {
      return <span style={{ color: '#666', fontSize: '0.9rem' }}>No data</span>;
    }
    const numericScore = parseFloat(score);
    const fullStars = Math.floor(numericScore);
    const hasHalfStar = (numericScore % 1) >= 0.5;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {[...Array(5)].map((_, index) => {
          if (index < fullStars) {
            return <span key={index} style={{ color: '#4CAF50', fontSize: '1.2rem' }}>⭐</span>;
          } else if (index === fullStars && hasHalfStar) {
            return <span key={index} style={{ color: '#4CAF50', fontSize: '1.2rem', opacity: 0.6 }}>⭐</span>;
          } else {
            return <span key={index} style={{ color: '#444', fontSize: '1.2rem' }}>⭐</span>;
          }
        })}
        <span style={{ marginLeft: '8px', fontSize: '0.95rem', fontWeight: '600', color: '#fff' }}>
          {numericScore.toFixed(2)}
        </span>
      </div>
    );
  };

  const getTierColor = (tier) => {
    const colors = {
      'Legendary': '#FFD700',
      'Excellent': '#4CAF50',
      'Good': '#2196F3',
      'Fair': '#FFA726',
      'Poor': '#f44336'
    };
    return colors[tier] || '#666';
  };

  const toggleExpand = (validatorId) => {
    setExpandedValidator(expandedValidator === validatorId ? null : validatorId);
  };

  const totalStake = validators.reduce((s, v) => s + v.total_stake, 0);
  const totalDelegators = validators.reduce((s, v) => s + v.delegator_count, 0);

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      padding: '40px 20px'
    },
    header: {
      maxWidth: '1400px',
      margin: '0 auto 40px',
      textAlign: 'center'
    },
    title: {
      fontSize: '3rem',
      fontWeight: '800',
      color: '#ffffff',
      marginBottom: '16px'
    },
    subtitle: {
      color: '#ffffff',
      fontSize: '1.1rem',
      opacity: 0.8
    },
    statsGrid: {
      maxWidth: '1400px',
      margin: '0 auto 40px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px'
    },
    statCard: {
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '16px',
      padding: '24px',
      color: '#ffffff',
      transition: 'all 0.3s ease',
      cursor: 'default'
    },
    statLabel: {
      fontSize: '0.9rem',
      opacity: 0.8,
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      color: '#ffffff'
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700',
      lineHeight: '1.2',
      color: '#ffffff'
    },
    searchContainer: {
      maxWidth: '1400px',
      margin: '0 auto 32px',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '12px',
      padding: '16px'
    },
    searchInput: {
      width: '100%',
      padding: '12px 20px',
      fontSize: '1rem',
      background: '#000000',
      border: '1px solid #ffffff',
      borderRadius: '8px',
      color: '#ffffff',
      outline: 'none'
    },
    tableContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '16px',
      overflow: 'hidden'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    thead: {
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      borderBottom: '2px solid #ffffff'
    },
    th: {
      padding: '20px',
      textAlign: 'left',
      color: '#ffffff',
      fontWeight: '600',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '1px'
    },
    tr: {
      borderBottom: '1px solid #ffffff',
      transition: 'all 0.2s ease'
    },
    td: {
      padding: '20px',
      color: '#ffffff',
      fontSize: '0.95rem'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    statusActive: {
      background: '#ffffff',
      color: '#000000',
      border: '1px solid #ffffff'
    },
    loadingContainer: {
      maxWidth: '1400px',
      margin: '100px auto',
      textAlign: 'center',
      color: '#ffffff'
    },
    loadingSpinner: {
      fontSize: '4rem',
      marginBottom: '24px',
      animation: 'spin 2s linear infinite'
    },
    emptyState: {
      maxWidth: '1400px',
      margin: '100px auto',
      textAlign: 'center',
      background: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      border: '1px solid #ffffff',
      borderRadius: '16px',
      padding: '60px 40px',
      color: '#ffffff'
    },
    emptyIcon: {
      fontSize: '5rem',
      marginBottom: '24px',
      opacity: 0.6
    },
    codeBlock: {
      background: '#000000',
      border: '1px solid #ffffff',
      borderRadius: '8px',
      padding: '16px',
      marginTop: '24px',
      fontFamily: 'monospace',
      fontSize: '0.9rem',
      color: '#ffffff'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}>⏳</div>
          <h3 style={{ fontSize: '1.5rem', margin: 0 }}>Loading Validators...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>⚠️</div>
          <h3 style={{ fontSize: '2rem', marginBottom: '16px' }}>Error Loading Validators</h3>
          <p style={{ fontSize: '1.1rem', opacity: 0.8 }}>{error}</p>
        </div>
      </div>
    );
  }

  if (validators.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <h3 style={{ fontSize: '2rem', marginBottom: '16px' }}>No Validators</h3>
          <p style={{ fontSize: '1.1rem', opacity: 0.8, marginBottom: '8px' }}>
            Create your first validator to get started!
          </p>
          <div style={styles.codeBlock}>
            avo operator validator stake --address YOUR_ADDRESS --amount 50000
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🏛️ Validators</h1>
        <p style={styles.subtitle}>
          Validator nodes with 12% APR - Securing network consensus
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Validators</div>
          <div style={styles.statValue}>{validators.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Staked</div>
          <div style={styles.statValue}>{formatStake(totalStake)}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Delegators</div>
          <div style={styles.statValue}>{totalDelegators}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Network APR</div>
          <div style={styles.statValue}>12.0%</div>
        </div>
      </div>

      <div style={styles.searchContainer}>
        <input
          type="text"
          style={styles.searchInput}
          placeholder="🔍 Search by Validator ID or Address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={styles.th}>Validator ID</th>
              <th style={styles.th}>Address</th>
              <th style={styles.th}>Total Stake</th>
              <th style={styles.th}>Delegators</th>
              <th style={styles.th}>APR</th>
              <th style={styles.th}>Rewards</th>
              <th style={styles.th}>Reputation</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredValidators.map((validator, index) => {
              const reputation = reputations[validator.address];
              const isExpanded = expandedValidator === validator.id;
              
              return (
                <React.Fragment key={validator.id}>
                  <tr 
                    style={{
                      ...styles.tr,
                      background: isExpanded ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) e.currentTarget.style.background = 'transparent';
                    }}
                    onClick={() => toggleExpand(validator.id)}
                  >
                    <td style={{...styles.td, fontFamily: 'monospace', fontSize: '0.85rem'}}>
                      <span style={{ marginRight: '8px' }}>{isExpanded ? '🔽' : '▶️'}</span>
                      #{validator.id}
                    </td>
                    <td style={{...styles.td, fontFamily: 'monospace'}}>
                      {validator.address.slice(0, 10)}...{validator.address.slice(-8)}
                    </td>
                    <td style={{...styles.td, fontWeight: '700', fontSize: '1.1rem'}}>
                      {formatStake(validator.total_stake)}
                    </td>
                    <td style={{...styles.td, color: '#ffffff', fontWeight: '600'}}>
                      {validator.delegator_count}
                    </td>
                    <td style={{...styles.td, color: '#ffffff', fontWeight: '600'}}>
                      {validator.apr.toFixed(1)}%
                    </td>
                    <td style={{...styles.td, color: '#ffffff', fontWeight: '600'}}>
                      {formatRewards(validator.rewards)}
                    </td>
                    <td style={{...styles.td}}>
                      {reputation ? renderStars(reputation.score) : <span style={{ color: '#666' }}>Pending...</span>}
                    </td>
                    <td style={styles.td}>
                      <span style={{...styles.statusBadge, ...styles.statusActive}}>
                        {validator.status}
                      </span>
                    </td>
                  </tr>
                  
                  {/* Expanded Details Panel */}
                  {isExpanded && reputation && (
                    <tr style={{ background: 'rgba(0,0,0,0.5)' }}>
                      <td colSpan="8" style={{ padding: '24px', borderTop: '1px solid rgba(76, 175, 80, 0.3)' }}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                          gap: '20px',
                          color: '#fff'
                        }}>
                          {/* Tier Badge */}
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', marginBottom: '16px' }}>
                            <span style={{
                              background: getTierColor(reputation.tier),
                              color: reputation.tier === 'Legendary' ? '#000' : '#fff',
                              padding: '8px 24px',
                              borderRadius: '20px',
                              fontSize: '1.2rem',
                              fontWeight: '700',
                              boxShadow: `0 0 20px ${getTierColor(reputation.tier)}40`
                            }}>
                              {reputation.tier} Tier
                            </span>
                          </div>

                          {/* Metrics */}
                          <div>
                            <div style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '4px' }}>Uptime Score</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, background: '#222', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
                                <div style={{ 
                                  width: `${(reputation.metrics.uptime_score * 100)}%`, 
                                  height: '100%', 
                                  background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.95rem', fontWeight: '600', minWidth: '50px' }}>
                                {(reputation.metrics.uptime_score * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '4px' }}>Performance</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, background: '#222', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
                                <div style={{ 
                                  width: `${(reputation.metrics.performance_score * 100)}%`, 
                                  height: '100%', 
                                  background: 'linear-gradient(90deg, #2196F3, #03A9F4)',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.95rem', fontWeight: '600', minWidth: '50px' }}>
                                {(reputation.metrics.performance_score * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '4px' }}>Attestation Rate</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, background: '#222', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
                                <div style={{ 
                                  width: `${(reputation.metrics.attestation_score * 100)}%`, 
                                  height: '100%', 
                                  background: 'linear-gradient(90deg, #9C27B0, #E91E63)',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.95rem', fontWeight: '600', minWidth: '50px' }}>
                                {(reputation.metrics.attestation_score * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '4px' }}>Participation</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, background: '#222', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
                                <div style={{ 
                                  width: `${(reputation.metrics.participation_score * 100)}%`, 
                                  height: '100%', 
                                  background: 'linear-gradient(90deg, #FF9800, #FFC107)',
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.95rem', fontWeight: '600', minWidth: '50px' }}>
                                {(reputation.metrics.participation_score * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>

                          {/* Slashing Info */}
                          <div style={{ gridColumn: '1 / -1', marginTop: '12px' }}>
                            <div style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '8px' }}>Slashing Events</div>
                            <div style={{ 
                              background: reputation.metrics.slashing_score < 1.0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                              border: `1px solid ${reputation.metrics.slashing_score < 1.0 ? '#f44336' : '#4CAF50'}`,
                              borderRadius: '8px',
                              padding: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ fontSize: '1rem' }}>
                                {reputation.metrics.slashing_score >= 1.0 ? '✅ No Slashing Events' : '⚠️ Has Slashing Events'}
                              </span>
                              <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                                Score: {(reputation.metrics.slashing_score * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        input::placeholder {
          color: rgba(255,255,255,0.6);
        }
      `}</style>
    </div>
  );
};

export default ValidatorsList;
